import i18n from './index';

/**
 * Locale-Helfer für den Admin-Bereich.
 *
 * Datums-, Zeit- und Währungsformate sollen der aktuell im Admin gewählten
 * Sprache folgen (DE/ES), statt fest auf 'de-DE' zu stehen. Diese Helfer lesen
 * die aktive i18next-Sprache und liefern passende Intl-Locales / Formatierungen.
 */

export type AdminLanguage = 'de' | 'es';

/** Aktuelle Admin-Sprache, robust auf 'de' | 'es' normalisiert. */
export const getAdminLanguage = (): AdminLanguage => {
  const lng = (i18n.language || 'de').slice(0, 2).toLowerCase();
  return lng === 'es' ? 'es' : 'de';
};

/** BCP-47-Locale für Intl/`toLocaleString` passend zur Admin-Sprache. */
export const getDateLocale = (): string => (getAdminLanguage() === 'es' ? 'es-ES' : 'de-DE');

/** Währungsbetrag (Default EUR) im aktuellen Locale formatieren. */
export const formatCurrency = (
  amount: number,
  currency: string = 'EUR',
  options: Intl.NumberFormatOptions = {},
): string =>
  new Intl.NumberFormat(getDateLocale(), {
    style: 'currency',
    currency,
    ...options,
  }).format(amount);

/** Zahl im aktuellen Locale formatieren. */
export const formatNumber = (value: number, options: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat(getDateLocale(), options).format(value);

/** Datum/Zeit aus Date oder ISO-String im aktuellen Locale formatieren. */
export const formatDateTime = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(getDateLocale(), options);
};

/** Nur-Datum-Variante. */
export const formatDate = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getDateLocale(), options);
};
